import {
  ApprovalWorkflowItemDto,
  AWAssetDto,
  AWTemplateDto,
  AWTemplateStageDto,
  UpdateAWDto,
  UpdateAWStageDto
} from '@api/Api';
import type {
  AwState,
  AwStateStage,
  AwStateStageMember
} from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import type { ApprovalModalAssetVersion } from '@redux/types/modalType';

type CreateAWInitialMemberValues = {
  email: string;
  name: string;
  me: boolean;
  pictureUrl?: string | null;
};

export function formattedAWAsset(
  assetVersion: ApprovalModalAssetVersion
): AWAssetDto {
  return {
    id: assetVersion.assetId,
    versionId: assetVersion.id,
    versionsCount: assetVersion.versionNumber,
    name: assetVersion.name,
    type: assetVersion.metadata.type,
    extension: assetVersion.extension,
    previewUrl: assetVersion.metadata.preview?.url ?? null,
    durationSeconds: null,
    hasAccess: true,
    accessDisabled: false,
    commentsCount: assetVersion.commentsCount,
    updatedAt: assetVersion.createdAt
  };
}

export function getEmptyApprovalWorkflowTemplateList(): AWTemplateDto[] {
  return new Array(3).fill('').map((_, index) => ({
    id: `${index}`,
    workspaceId: '',
    name: `Test template name ${index + 1}`,
    lockDecisions: false,
    disableNonApprovedDownloads: false,
    newVersionBehavior: 'pause' as AWTemplateDto['newVersionBehavior'],
    password: null,
    customMessage: null,
    allowAllVersions: false,
    allowAllComments: true,
    stages: [
      {
        id: `${index}`,
        name: '',
        completionCondition:
          'all_decisions_submitted' as AWTemplateStageDto['completionCondition'],
        members: []
      }
    ],
    createdAt: ''
  }));
}

export function getCreateAWInitialStage(
  member: CreateAWInitialMemberValues & Partial<AwStateStageMember>
): AwStateStage {
  return {
    id: `${Date.now()}_${Math.random()}`,
    name: 'Name approval routing stage',
    completionCondition: 'all_decisions_submitted',
    members: [getCreateAWInitialMember(member)]
  };
}

export function getCreateAWInitialMember(
  member: CreateAWInitialMemberValues & Partial<AwStateStageMember>
): AwStateStageMember {
  return {
    email: member?.email || '',
    name: member.name,
    me: member.me,
    picture: {
      url: member.pictureUrl || ''
    },
    canUploadNewVersion: member?.canUploadNewVersion ?? true,
    canManageAW: member?.canManageAW ?? true,
    canComment: member?.canComment ?? true,
    canSubmitDecision: member?.canSubmitDecision ?? false,
    canDownload: member?.canDownload ?? true,
    hasCommentPriority: member?.hasCommentPriority ?? false,
    hasDominatingDecision: member?.hasDominatingDecision ?? true
  };
}

// STAGES
const MEMBER_FIELDS: (keyof AwStateStageMember)[] = [
  'name',
  'email',
  'canComment',
  'canSubmitDecision',
  'canUploadNewVersion',
  'canManageAW',
  'canDownload',
  'hasCommentPriority',
  'hasDominatingDecision'
];

const isSameMember = (a: AwStateStageMember, b: AwStateStageMember): boolean =>
  MEMBER_FIELDS.every((key) => a[key] === b[key]);

function diffMembers(prev: AwStateStageMember[], curr: AwStateStageMember[]) {
  const prevMap = new Map(prev.map((member) => [member.email, member]));
  const currMap = new Map(curr.map((member) => [member.email, member]));

  const added = [];
  const removed = [];
  const updated = [];

  for (const [email, currMember] of currMap) {
    const prevMember = prevMap.get(email);
    if (!prevMember) {
      added.push(currMember);
    } else if (!isSameMember(prevMember, currMember)) {
      updated.push(currMember);
    }
  }

  for (const [email, prevMember] of prevMap) {
    if (!currMap.has(email)) removed.push(prevMember);
  }

  return { added, removed, updated };
}

export function diffStages(prev: AwStateStage[], curr: AwStateStage[]) {
  const prevMap = new Map(prev.map((stage) => [stage.id, stage]));
  const currMap = new Map(curr.map((stage) => [stage.id, stage]));

  const added: AwStateStage[] = [];
  const removed: AwStateStage[] = [];
  const updated: UpdateAWStageDto[] = [];

  for (const [id, currStage] of currMap) {
    const prevStage = prevMap.get(id);
    if (!prevStage) {
      added.push(currStage);
    }

    if (prevStage) {
      const baseChanged =
        currStage.name !== prevStage.name ||
        currStage.completionCondition !== prevStage.completionCondition ||
        currStage.deadline !== prevStage.deadline;
      const membersDiff = diffMembers(prevStage.members, currStage.members);
      if (
        baseChanged ||
        membersDiff.added.length ||
        membersDiff.removed.length ||
        membersDiff.updated.length
      ) {
        updated.push({
          ...currStage,
          members: {
            add: membersDiff.added,
            remove: membersDiff.removed.map((member) => member.email),
            update: membersDiff.updated
          }
        });
      }
    }
  }

  for (const [id, prevStage] of prevMap) {
    if (!currMap.has(id)) removed.push(prevStage);
  }

  return { added, removed, updated };
}
type SharedKeys = Extract<keyof AwState, keyof UpdateAWDto>;
const AW_FIELDS: SharedKeys[] = [
  'name',
  'lockDecisions',
  'disableNonApprovedDownloads',
  'newVersionBehavior',
  'password',
  'customMessage',
  'allowAllVersions',
  'allowAllComments'
];

export function diffApproval({
  currentAW,
  updateAW,
  selectedAssetVersions
}: {
  currentAW: ApprovalWorkflowItemDto;
  updateAW: AwState;
  selectedAssetVersions: AWAssetDto[];
}): UpdateAWDto {
  const getDiffStages = diffStages(currentAW.stages, updateAW.stages);
  const removeAssetIds = currentAW.assets
    .filter(
      (awAsset) =>
        !selectedAssetVersions.some(
          (selectedAsset) => selectedAsset.id === awAsset.id
        )
    )
    .map((awAsset) => awAsset.id);
  const addAssetIds = selectedAssetVersions
    .filter(
      (assetVersion) =>
        !currentAW.assets.some(
          (selectedAsset) => selectedAsset.id === assetVersion.id
        )
    )
    .map((assetVersion) => assetVersion.id);
  const dynamicFields = AW_FIELDS.reduce((acc, key) => {
    if (updateAW[key] !== currentAW[key]) {
      acc[key] = updateAW[key];
    }
    return acc;
  }, {} as Record<string, any>);
  return {
    id: currentAW.id,
    ...dynamicFields,
    assets: {
      add: addAssetIds,
      remove: removeAssetIds
    },
    stages: {
      add: getDiffStages.added,
      update: getDiffStages.updated,
      remove: getDiffStages.removed.map((i) => i.id)
    }
  };
}
